import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send } from "lucide-react";

export const RoomChat = ({ roomId }) => {
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const endRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/rooms/${roomId}/messages`);
      setRoom(data.room);
      setMessages(data.messages);
    } catch (e) {
      console.error("Failed to load room messages", e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [roomId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    setMessages((m) => [...m, { sender_id: user.user_id, sender_name: user.name, text }]);
    try {
      await api.post(`/rooms/${roomId}/messages`, { text });
    } catch (e) {
      console.error("Failed to send message", e);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {room && (
        <div className="px-4 py-3 border-b border-white/10">
          <div className="font-semibold text-white text-sm">{room.deal_title}</div>
          <div className="text-xs text-slate-500">
            {user?.user_id === room.owner_id ? room.investor_name : room.owner_name}
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="room-messages">
        {messages.length === 0 && (
          <p className="text-xs text-slate-500 text-center pt-8">
            Start negotiating. Agree the terms here, then lock it in with an offer & signed agreement.
          </p>
        )}
        {messages.map((m, i) => {
          const mine = m.sender_id === user?.user_id;
          return (
            <div key={`${m.created_at || "local"}-${i}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${mine ? "bg-amber-600 text-white" : "bg-[#1A202C] text-slate-200 border border-white/10"}`}>
                {!mine && <div className="text-[10px] text-slate-400 mb-0.5">{m.sender_name}</div>}
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-white/10 flex gap-2">
        <Input
          data-testid="room-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message…"
          className="bg-[#0B0E14] border-white/10 text-white h-10 focus-visible:ring-amber-500"
        />
        <Button data-testid="room-send" onClick={send} className="bg-amber-600 hover:bg-amber-700 text-white h-10 px-4 rounded-lg">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
